import React, { type ReactElement } from 'react';
import { styled } from '@compiled/react';
import Spinner from '@atlaskit/spinner';
import { useResource } from '@atlassian/jira-router';
import { devOpsReportPreconditionResource } from '@atlassian/jira-router-resources-devops-report-group/src/services/index.tsx';
import type { PreconditionResult } from '@atlassian/jira-router-resources-devops-report-group/src/types.tsx';
import type { ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { ViewDeploymentFrequencyReportExpectedErrorExperienceSuccessTracker } from '@atlassian/jira-software-deployment-frequency-report/src/services/experience-tracker/index.tsx';
import { NoPermissionPage } from './not-available-page';

type Props = {
	children: ReactElement;
	projectKey: ProjectKey;
};

export const Loading = () => (
	<LoadingWrapper>
		<Spinner size="large" />
	</LoadingWrapper>
);

const isConditionSatisfied = ({
	hasViewDevToolsPermission,
	hasViewAggregateDataPermission,
}: PreconditionResult): boolean => hasViewDevToolsPermission && hasViewAggregateDataPermission;

export const PreconditionCheck = ({ children, projectKey }: Props) => {
	const {
		data: preconditionData,
		error: preconditionError,
		loading: preconditionLoading,
	} = useResource(devOpsReportPreconditionResource);

	if (preconditionError) {
		throw preconditionError;
	}

	if (preconditionLoading || preconditionData == null) {
		return <Loading />;
	}

	if (isConditionSatisfied(preconditionData)) {
		return children;
	}

	return (
		<>
			<ViewDeploymentFrequencyReportExpectedErrorExperienceSuccessTracker reason="User does not have permission to view the page" />
			<NoPermissionPage projectKey={projectKey} />
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LoadingWrapper = styled.div({
	width: '100%',
	height: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});
